import FJUtil from './FJUtil';
import { FJDecoderManager } from '@fj/codec';
import FJAudioUtil from './FJAudioUtil.js';
import FJLoadLocalforageStore from '@src/store/FJLoadLocalforageStore.js';
import FJMathUtil from './FJMathUtil.js';

/**
 * 边缘条带内像素平均相对亮度（0~1），在 sRGB 存储值上做快速加权和（非严格线性化，适合阈值分割）。
 * 四边划分：全宽顶/底带 + 中间区域的左/右竖带，避免四角重复累计。
 */
function fjEdgeRegionMeanLuminance(imageData, width, height, edgeRatio, pixelStride) {
    const bandY = Math.max(1, Math.floor(height * edgeRatio));
    const bandX = Math.max(1, Math.floor(width * edgeRatio));
    const stride = Math.max(1, pixelStride | 0);
    let sum = 0;
    let count = 0;

    for (let y = 0; y < height; y += stride) {
        const inTop = y < bandY;
        const inBottom = y >= height - bandY;
        for (let x = 0; x < width; x += stride) {
            const inLeft = x < bandX && y >= bandY && y < height - bandY;
            const inRight = x >= width - bandX && y >= bandY && y < height - bandY;
            if (!inTop && !inBottom && !inLeft && !inRight) {
                continue;
            }
            const i = (y * width + x) << 2;
            const r = imageData[i];
            const g = imageData[i + 1];
            const b = imageData[i + 2];
            sum += (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
            count += 1;
        }
    }
    return count > 0 ? sum / count : 0;
}

/** 将当前 video 帧缩放到小画布后，返回边缘区域平均亮度（0~1）。失败返回 0。 */
function fjMeasureLightBackgroundLuminanceFromVideoFrame(video, options) {
    const sw = video.videoWidth;
    const sh = video.videoHeight;
    if (!sw || !sh) {
        return 0;
    }
    const maxLongEdge = options.maxLongEdge ?? 96;
    let dw = sw;
    let dh = sh;
    if (typeof maxLongEdge === 'number' && maxLongEdge > 0) {
        const scaled = FJMathUtil.scaleToMaxLongEdge(sw, sh, maxLongEdge);
        dw = scaled.width;
        dh = scaled.height;
    }
    const canvas = document.createElement('canvas');
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
        return 0;
    }
    try {
        ctx.drawImage(video, 0, 0, sw, sh, 0, 0, dw, dh);
    } catch {
        return 0;
    }
    let imageData;
    try {
        imageData = ctx.getImageData(0, 0, dw, dh);
    } catch {
        return 0;
    }
    return fjEdgeRegionMeanLuminance(imageData.data, dw, dh, options.edgeRatio ?? 0.12, options.pixelStride ?? 2);
}

const FJVideoUtil = {
    couldUseWebCodecs(type) {
        if (!window.VideoEncoder || !window.VideoDecoder) {
            return false;
        }

        const chromeVersion = FJUtil.getChromeVersion();
        if (chromeVersion < 92) {
            return false;
        }

        const maxSupportChromeVersion = 109;
        const maxSupportDecodeChromeVersion = Infinity;
        const maxSupportEncodeChromeVersion = Infinity;

        if (type === 'decode') {
            return chromeVersion <= maxSupportDecodeChromeVersion;
        } else if (type === 'encode') {
            return chromeVersion <= maxSupportEncodeChromeVersion;
        } else {
            return chromeVersion <= maxSupportChromeVersion;
        }
    },
    /**
     * @description 返回解码视频帧类型
     * @returns
     */
    getDecodeVideoFrameType: function () {
        if (FJUtil.getBrowser() === 'FF') {
            return 'IMAGEBITMAP';
        }

        if (this.couldUseWebCodecs('decode') && typeof VideoFrame !== 'undefined') {
            return 'VIDEOFRAME';
        }
        if (
            typeof ImageBitmap !== 'undefined' &&
            typeof createImageBitmap !== 'undefined' &&
            typeof OffscreenCanvas !== 'undefined'
        ) {
            return 'IMAGEBITMAP';
        }
        return 'TYPEDARRAY';
    },

    getVideoDurationWithUrl: function (url) {
        return new Promise((resolve, reject) => {
            let video = document.createElement('video');

            function addEvent() {
                FJUtil.addEvent(video, 'loadeddata', onLoaded);
                FJUtil.addEvent(video, 'error', onError);
            }

            function removeEvent() {
                FJUtil.removeEvent(video, 'loadeddata', onLoaded);
                FJUtil.removeEvent(video, 'error', onError);
                video.src = '';
                video = null;
            }

            function onLoaded() {
                let duration = video.duration;
                removeEvent();
                resolve(duration);
            }

            function onError(e) {
                removeEvent();
                console.log(e);
                reject(new Error('getAudioDurationWithUrl error'));
            }

            addEvent();
            video.src = url;
        });
    },
    getAudioBufferFromVideo: async (file, dur) => {
        try {
            const arrayBuffer = await FJDecoderManager.decodeAudioOverall(file, {
                start: 0,
                end: dur,
                samplerate: FJUtil.getAudioSampleRate(),
                mode: 'FFMPEG',
                speed: 1,
                fileType: 'video',
                ignoreNoValidDuration: true,
            });
            return arrayBuffer;
        } catch (e) {
            return null;
        }
    },
    getAudioFromVideo: async function (file, options = {}) {
        const { format = 'wav', decodeCallback } = options;
        try {
            const blobUrl = URL.createObjectURL(file);
            const videoDuration = await this.getVideoDurationWithUrl(blobUrl);
            const arrayBuffer = await FJDecoderManager.decodeAudioOverall(file, {
                start: 0,
                end: videoDuration,
                samplerate: FJUtil.getAudioSampleRate(),
                mode: 'FFMPEG',
                speed: 1,
                fileType: 'video',
                ignoreNoValidDuration: true,
            });
            decodeCallback?.();
            if (format === 'wav') {
                const wavBuffer = FJAudioUtil.encodeWAV(new Float32Array(arrayBuffer));
                const mimeType = 'audio/wav';
                const blob = new Blob([new DataView(wavBuffer)], {
                    type: mimeType,
                });
                const audioFile = new File([blob], FJUtil.getBaseName(file.name) + '.wav', { type: mimeType });
                return audioFile;
            } else if (format === 'mp3') {
                const mp3Data = await FJAudioUtil.encodeMp3(arrayBuffer);
                const mimeType = 'audio/mp3';
                const blob = new Blob(mp3Data, { type: mimeType });

                const audioFile = new File([blob], FJUtil.getBaseName(file.name) + '.mp3', { type: mimeType });
                return audioFile;
            }
        } catch (e) {
            throw new Error(e);
        }
    },

    /**
     * 判断 `decodeAudioOverall` 等返回的 PCM 是否含有可感知响度（非全程接近静音）。
     * 与 `encodeWAV(new Float32Array(arrayBuffer))` 一致：Float32、立体声交错 L,R,L,R…
     * 单次线性扫描：累加平方得 RMS，并跟踪峰值；不分配中间数组。
     *
     * @param {ArrayBuffer|Float32Array} pcm
     * @param {object} [options]
     * @param {number} [options.rmsThreshold=0.0008] 整体 RMS 达到或超过则视为有声
     * @param {number} [options.peakThreshold=0.012] 任一采样绝对值达到或超过则视为有声（利于极短脉冲）
     * @param {number} [options.frameStride=1] 立体声帧步进，每帧 2 个 float；2 表示隔帧采样（约减半运算，可能漏检极短音）
     * @param {boolean} [options.leftChannelOnly=false] 仅扫左声道（约减半运算；仅右声道有声时可能漏检）
     * @returns {boolean}
     */
    hasAudiblePcmFromDecodeBuffer: function (pcm, options = {}) {
        const rmsThreshold = options.rmsThreshold ?? 0.0008;
        const peakThreshold = options.peakThreshold ?? 0.012;
        const frameStride = Math.max(1, options.frameStride | 0);
        const leftChannelOnly = !!options.leftChannelOnly;

        const f32 = pcm instanceof Float32Array ? pcm : new Float32Array(pcm);
        const len = f32.length;
        if (len < 1) {
            return false;
        }

        let sumSq = 0;
        let count = 0;
        let peak = 0;
        const frameStep = 2 * frameStride;

        if (leftChannelOnly) {
            for (let i = 0; i < len; i += frameStep) {
                const v = f32[i];
                const a = v < 0 ? -v : v;
                if (a > peak) {
                    peak = a;
                }
                sumSq += v * v;
                count += 1;
            }
        } else {
            for (let i = 0; i < len; i += frameStep) {
                const l = f32[i];
                const al = l < 0 ? -l : l;
                if (al > peak) {
                    peak = al;
                }
                sumSq += l * l;
                count += 1;
                const ri = i + 1;
                if (ri < len) {
                    const r = f32[ri];
                    const ar = r < 0 ? -r : r;
                    if (ar > peak) {
                        peak = ar;
                    }
                    sumSq += r * r;
                    count += 1;
                }
            }
        }

        if (count === 0) {
            return false;
        }
        const meanSq = sumSq / count;
        const rms = meanSq > 0 ? Math.sqrt(meanSq) : 0;
        return rms >= rmsThreshold || peak >= peakThreshold;
    },

    /**
     * 解码视频前若干秒 PCM 并判断是否含可感知响度（用于上传前校验，避免无音轨/全程静音进入流程）。
     * @param {File} file
     * @param {number} durationSeconds 视频总时长（秒）
     * @param {object} [opts]
     * @param {number} [opts.maxProbeSeconds=90] 只解码 [0, min(duration, maxProbeSeconds)]，减轻长视频开销
     * @param {object} [opts.pcmOptions] 传给 `hasAudiblePcmFromDecodeBuffer` 的阈值等
     * @returns {Promise<boolean>}
     */
    hasAudibleAudioInVideoFile: async function (file, durationSeconds, opts = {}) {
        const maxProbe = opts.maxProbeSeconds ?? 90;
        const end =
            Number.isFinite(durationSeconds) && durationSeconds > 0 ? Math.min(durationSeconds, maxProbe) : maxProbe;
        const buffer = await this.getAudioBufferFromVideo(file, end);
        if (buffer == null || buffer.byteLength < 8) {
            return false;
        }
        return this.hasAudiblePcmFromDecodeBuffer(buffer, opts.pcmOptions);
    },

    /**
     * @description 加载视频
     * @param {File|Blob|string} fileOrUrl 本地视频 File/Blob，或视频 URL
     * @returns
     */
    loadVideo: function (fileOrUrl) {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            const isUrlString = typeof fileOrUrl === 'string';
            const url = isUrlString ? fileOrUrl : URL.createObjectURL(fileOrUrl);
            if (isUrlString && /^https?:\/\//i.test(fileOrUrl)) {
                video.crossOrigin = 'anonymous';
            }

            const cleanup = () => {
                if (!isUrlString) {
                    URL.revokeObjectURL(url);
                }
                this.releaseVideo(video);
            };

            video.onloadedmetadata = () => {
                const name = isUrlString ? FJUtil.getBaseName(fileOrUrl) : (fileOrUrl.name ?? '');
                resolve({
                    width: video.videoWidth,
                    height: video.videoHeight,
                    duration: video.duration,
                    src: video.src,
                    name,
                });
                cleanup();
            };
            video.onerror = () => {
                cleanup();
                reject(false);
            };
            video.src = url;
        });
    },
    checkVideoFileIsSupport: async function (file) {
        try {
            const { width, height, duration } = await this.loadVideo(file);
            if (width === 0 || height === 0 || duration === 0) {
                return false;
            }
            return true;
        } catch (e) {
            return false;
        }
    },

    /**
     * 从视频文件或 URL 中截取指定时间点的画面为缩略图（Blob）。
     * @param {File|Blob|string} fileOrUrl 本地视频 File/Blob，或视频 URL
     * @param {number} timeInSeconds 时间点（秒），会钳制到 [0, duration)
     * @param {{
     *   mimeType?: string,
     *   quality?: number,
     *   maxLongEdge?: number,
     * }} [options] mimeType 默认 image/jpeg；quality 仅对 jpeg/webp 有效，默认 0.92；**maxLongEdge** 为正数时，输出图最长边不超过该像素（等比缩放）
     * @returns {Promise<Blob>}
     */
    getThumbnailAtTime: function (fileOrUrl, timeInSeconds = 0, options = {}) {
        const mimeType = options.mimeType ?? 'image/jpeg';
        const quality = options.quality ?? 0.92;
        const maxLongEdgeOpt = options.maxLongEdge;

        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.muted = true;
            video.playsInline = true;
            video.preload = 'auto';
            const isUrlString = typeof fileOrUrl === 'string';
            const url = isUrlString ? fileOrUrl : URL.createObjectURL(fileOrUrl);
            if (isUrlString && /^https?:\/\//i.test(fileOrUrl)) {
                video.crossOrigin = 'anonymous';
            }

            const cleanup = () => {
                if (!isUrlString) {
                    URL.revokeObjectURL(url);
                }
                this.releaseVideo(video);
            };

            const fail = err => {
                cleanup();
                reject(err);
            };

            let settled = false;

            const captureFrame = () => {
                if (settled) return;
                const w = video.videoWidth;
                const h = video.videoHeight;
                if (!w || !h) {
                    fail(new Error('Invalid video dimensions for thumbnail'));
                    return;
                }
                let outW = w;
                let outH = h;
                if (typeof maxLongEdgeOpt === 'number' && maxLongEdgeOpt > 0) {
                    const scaled = FJMathUtil.scaleToMaxLongEdge(w, h, maxLongEdgeOpt);
                    outW = scaled.width;
                    outH = scaled.height;
                }
                const canvas = document.createElement('canvas');
                canvas.width = outW;
                canvas.height = outH;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    fail(new Error('Canvas 2d context unavailable'));
                    return;
                }
                try {
                    ctx.drawImage(video, 0, 0, w, h, 0, 0, outW, outH);
                } catch (e) {
                    fail(e instanceof Error ? e : new Error('drawImage failed'));
                    return;
                }
                canvas.toBlob(
                    blob => {
                        if (settled) return;
                        settled = true;
                        cleanup();
                        if (!blob) {
                            reject(new Error('Failed to encode thumbnail'));
                            return;
                        }
                        resolve(blob);
                    },
                    mimeType,
                    mimeType === 'image/png' ? undefined : quality,
                );
            };

            const seekToAndCapture = () => {
                const duration = video.duration;
                if (!Number.isFinite(duration) || duration <= 0) {
                    fail(new Error('Invalid video duration'));
                    return;
                }
                let t = Number(timeInSeconds);
                const wantFirstFrame = !Number.isFinite(t) || t <= 0;
                if (!Number.isFinite(t)) {
                    t = 0;
                }
                const epsilon = 1 / 60;
                t = Math.max(0, Math.min(t, Math.max(0, duration - epsilon)));

                const afterSeek = () => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(captureFrame);
                    });
                };

                // t<=0 时取首帧：部分浏览器对 currentTime=0 不触发 seeked，loadeddata 后即可绘制
                if (wantFirstFrame) {
                    video.currentTime = 0;
                    afterSeek();
                    return;
                }

                const onSeeked = () => {
                    video.removeEventListener('seeked', onSeeked);
                    afterSeek();
                };
                video.addEventListener('seeked', onSeeked);
                video.currentTime = t;
            };

            video.addEventListener(
                'loadeddata',
                () => {
                    try {
                        seekToAndCapture();
                    } catch (err) {
                        fail(err instanceof Error ? err : new Error(String(err)));
                    }
                },
                { once: true },
            );

            video.onerror = () => fail(new Error('Failed to load video for thumbnail'));

            video.src = url;
        });
    },

    /**
     * 基于画面四周边缘条带的平均亮度，判断视频是否偏「浅色背景」（方案1：假设背景多分布在四周，主体多在中央）。
     * 流程：下采样 → 仅统计边缘区域像素 → 与亮度阈值比较；对本地视频在总时长 **10% / 30% / 50% / 70% / 90%** 五个时刻各采一帧，**全部**达标才为 true。
     *
     * @param {File|Blob|string|HTMLVideoElement} source 本地 `File`/`Blob`、视频 URL，或已具备当前画面的 `HTMLVideoElement`（`readyState >= HAVE_CURRENT_DATA`）
     * @param {object} [options]
     * @param {number} [options.maxLongEdge=96] 分析画布最长边（像素），越小越快
     * @param {number} [options.edgeRatio=0.12] 顶/底/左/右侧条带占画面宽或高的比例
     * @param {number} [options.luminanceThreshold=0.62] 边缘平均亮度阈值（0~1），达标视为该帧浅色背景
     * @param {number} [options.pixelStride=2] 边缘像素抽样步长，越大越快、统计略粗
     * @returns {Promise<boolean>}
     */
    isLightBackgroundVideo: function (source, options = {}) {
        const threshold = options.luminanceThreshold ?? 0.62;
        const haveCurrentData = typeof HTMLVideoElement !== 'undefined' ? HTMLVideoElement.HAVE_CURRENT_DATA : 2;

        if (typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement) {
            if (source.readyState < haveCurrentData) {
                return Promise.resolve(false);
            }
            const lum = fjMeasureLightBackgroundLuminanceFromVideoFrame(source, options);
            return Promise.resolve(lum >= threshold);
        }

        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.muted = true;
            video.playsInline = true;
            video.preload = 'auto';
            const isUrlString = typeof source === 'string';
            const url = isUrlString ? source : URL.createObjectURL(source);
            if (isUrlString && /^https?:\/\//i.test(source)) {
                video.crossOrigin = 'anonymous';
            }

            const cleanup = () => {
                if (!isUrlString) {
                    URL.revokeObjectURL(url);
                }
                this.releaseVideo(video);
            };

            const fail = err => {
                cleanup();
                reject(err instanceof Error ? err : new Error(String(err)));
            };

            const measureAfterSeek = targetTimeSeconds => {
                return new Promise((res, rej) => {
                    const duration = video.duration;
                    if (!Number.isFinite(duration) || duration <= 0) {
                        rej(new Error('Invalid video duration'));
                        return;
                    }
                    let t = Number(targetTimeSeconds);
                    if (!Number.isFinite(t)) {
                        t = 0;
                    }
                    const epsilon = 1 / 60;
                    t = Math.max(0, Math.min(t, Math.max(0, duration - epsilon)));

                    const runMeasure = () => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                res(fjMeasureLightBackgroundLuminanceFromVideoFrame(video, options));
                            });
                        });
                    };

                    const wantHead = t <= epsilon;
                    if (wantHead) {
                        video.currentTime = 0;
                        runMeasure();
                        return;
                    }
                    const onSeeked = () => {
                        video.removeEventListener('seeked', onSeeked);
                        runMeasure();
                    };
                    video.addEventListener('seeked', onSeeked);
                    video.currentTime = t;
                });
            };

            video.addEventListener(
                'loadeddata',
                async () => {
                    try {
                        const duration = video.duration;
                        if (!Number.isFinite(duration) || duration <= 0) {
                            cleanup();
                            resolve(false);
                            return;
                        }
                        const sampleRatios = [0.1, 0.3, 0.5, 0.7, 0.9];
                        const times = sampleRatios.map(r => duration * r);
                        for (let ti = 0; ti < times.length; ti++) {
                            const L = await measureAfterSeek(times[ti]);
                            if (L < threshold) {
                                cleanup();
                                resolve(false);
                                return;
                            }
                        }
                        cleanup();
                        resolve(true);
                    } catch (e) {
                        fail(e instanceof Error ? e : new Error(String(e)));
                    }
                },
                { once: true },
            );

            video.onerror = () => fail(new Error('Failed to load video for light background probe'));
            video.src = url;
        });
    },

    /**
     * @description 获取视频元数据信息
     * @param {File} file
     * @returns
     */
    getMetadataInfo: async function (file) {
        try {
            const info = await FJDecoderManager.getMetadataInfo(file);
            if (info.error) {
                throw new Error(info.error);
            }
            return info;
        } catch (e) {
            return null;
        }
    },

    /**
     * @description 判断视频文件是否是特殊格式 || 是否需要转码，目前只判断是否是HLG视频 用于视频去背景拦截不支持视频格式
     * @param {*} file
     * @returns
     */
    checkVideoFileIsSpecialFormat: function (file) {
        return new Promise(async resolve => {
            try {
                const mediaInfo = await this.getMetadataInfo(file);
                if (!mediaInfo) {
                    resolve(true);
                    return;
                }
                const { streams } = mediaInfo;

                let isHDR = false;
                for (let i = 0; i < streams.length; i++) {
                    const { color_transfer } = streams[i];
                    if (color_transfer) {
                        // 1. 检查色彩转换特性
                        isHDR = color_transfer === 'arib-std-b67'; // HLG
                        // color_transfer === 'smpte2084' || // HDR10/HDR10+
                        // color_transfer === 'smpte428'; // Dolby Vision
                    }
                    if (isHDR) {
                        resolve(true);
                        return;
                    }
                    // if (color_space) {
                    //     // 2. 检查色彩空间
                    //     isHDR = color_space === 'bt2020nc' || color_space === 'bt2020c';
                    // }
                    // if (isHDR) {
                    //     resolve(true);
                    //     return;
                    // }
                    // if (color_primaries) {
                    //     // 3. 检查色彩原色
                    //     isHDR = color_primaries === 'bt2020';
                    // }
                    // if (isHDR) {
                    //     resolve(true);
                    //     return;
                    // }
                }

                resolve(false);

                // // 判断是不是带有透明通道的mov视频
                // let isAlphaOfMov = false;

                // // 检查文件扩展名是否为mov
                // const fileName = file.name.toLowerCase();
                // const isMovFile = fileName.endsWith('.mov');

                // if (isMovFile) {
                //     // 检查视频流中是否包含alpha通道
                //     for (let i = 0; i < streams.length; i++) {
                //         const stream = streams[i];
                //         if (
                //             stream.pix_fmt &&
                //             (stream.pix_fmt.includes('rgba') ||
                //                 stream.pix_fmt.includes('yuva') ||
                //                 stream.pix_fmt.includes('_alpha'))
                //         ) {
                //             isAlphaOfMov = true;
                //             break;
                //         }
                //     }
                // }

                // resolve(isAlphaOfMov);
            } catch (e) {
                resolve(false);
            }
        });
    },
    releaseVideo: function (mediaEl) {
        if (!mediaEl) return;
        mediaEl.pause();
        if (mediaEl.src.startsWith('blob:')) {
            URL.revokeObjectURL(mediaEl.src); // 释放 Blob 资源（关键！避免永久占用内存）
        }
        mediaEl.src = '';
        mediaEl.load(); // 强制释放解码器和 GPU 纹理内存
    },
    getHashBuffer: function (source, threshold = 1073741824) {
        let size = source.size ? source.size : source.length;

        // 默认超过1G会进行裁剪
        if (size > threshold) {
            let offset = 0; // Math.floor((size - threshold)/2);
            source = source.slice(offset, offset + threshold);
        }

        if (source.arrayBuffer) {
            return source.arrayBuffer();
        } else {
            return this.convertToArrayBuffer(source);
        }
    },
    saveFileListToLocalStorage: async function (files) {
        if (files.length < 1) {
            return;
        }

        let input_files = []; // localstorage中存储的文件信息
        let tasks = []; // localforage中存储的文件

        for (let i = 0; i < files.length; i++) {
            let fileData = files[i];
            const { key: fileKey, file, isFile = false } = fileData;
            // 判断文件大小不能超过3G
            if (file.size > 1073741824 * 3) {
                console.log('size > 1073741824 * 3');
                continue;
            }
            const key = fileKey ?? (await FJUtil.hashEncode(file));
            input_files[i] = {
                key,
                info: {
                    type: file.type,
                    size: file.size,
                    name: file.name,
                },
            };

            let task = [];

            let mainFile = isFile ? file : new File([file], file.name, { type: file.type });

            task = FJLoadLocalforageStore.setItem(key, {
                data: mainFile,
                type: file.type,
                size: file.size,
                name: file.name,
            });

            tasks.push(task);
        }

        if (tasks.length > 0) {
            await Promise.all(tasks);
        }
        return input_files;
    },
    getFileListFromLocalStorage: async function (key) {
        return FJLoadLocalforageStore.getItem(key);
    },
    removeFileListFromLocalStorage: async function (key) {
        return FJLoadLocalforageStore.removeItem(key);
    },
};

export default FJVideoUtil;
